import { COMPANY, INDIVIDUAL } from '@constants/customerTypes'
import { postCodeMask } from '@helpers/masks'
import {
  getFilteredCountries,
  getMappedCountries,
  getVATLabel,
  getVATTooltip,
  isEuropeanUnion
} from '@pages/Checkout/helpers/billing'
import { FormikType, InitialValuesTypes } from '@pages/Checkout/routes/contexts'
import { CountriesType } from '@services/payments/useGetCountries'
import {
  Icon,
  Input,
  MaskedInput,
  Paragraph,
  Radio,
  Select,
  Spacer,
  Spreader,
  Tooltip
} from '@ui-kit'
import { FC, Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from 'simple-flexbox'

import { EmptyMessage } from './components/EmptyMessage'
import styles from './Form.module.scss'

interface FormProps {
  formik: FormikType
  countriesList: CountriesType[]
}

export const Form: FC<FormProps> = ({ formik, countriesList }) => {
  const { t } = useTranslation()

  const [coutrySearchPhrase, setCountrySearchPhrase] = useState('')

  const { values, handleChange, setFieldValue, handleBlur, errors, touched } =
    formik

  const hasFieldError = (name: keyof InitialValuesTypes) =>
    !!(touched[name] && errors[name])

  const handleSearchCountry = (value: string) => {
    setCountrySearchPhrase(value)
  }

  const handleOnChangeCountry = (country: string) => {
    setFieldValue('country', country)

    setCountrySearchPhrase('')
  }

  const countries = getFilteredCountries({
    countries: countriesList,
    searchPhrase: coutrySearchPhrase
  })

  const {
    customerType,
    companyName,
    country,
    nameAndSurname,
    vat,
    address,
    postCode,
    city
  } = values

  const isEuropeanUnionCountry = isEuropeanUnion({
    countries: countriesList,
    country
  })

  return (
    <Fragment>
      <Row>
        <Radio
          id={COMPANY}
          label={t('creditcardstep.form.company')}
          field={{
            name: 'customerType',
            value: customerType,
            onChange: handleChange
          }}
        />

        <Spreader spread={70} />

        <Radio
          id={INDIVIDUAL}
          label={t('creditcardstep.form.individual')}
          field={{
            name: 'customerType',
            value: customerType,
            onChange: handleChange
          }}
        />
      </Row>

      <Spacer space={20} />

      <Paragraph color='neutral-6' lineHeight='normal'>
        {t('creditcardstep.form.country')}
      </Paragraph>

      <Select
        id='country'
        value={country}
        onChange={handleOnChangeCountry}
        i18n={{
          placeholder: t('creditcardstep.form.country')
        }}
        options={getMappedCountries(countries)}
        overflowStyle={{ maxHeight: '220px' }}
        searcher={{
          shouldRender: true,
          placeholder: t(
            'landings.dashboard.tabs.events.heading.filters.country.name'
          ),
          liveChanges: true,
          onChange: handleSearchCountry
        }}
        emptyMessage={<EmptyMessage />}
      />

      <Spacer space={40} />

      {customerType === COMPANY ? (
        <div className={styles['form__company-fields']}>
          <Input
            id='companyName'
            value={companyName}
            i18n={{
              placeholder: t('creditcardstep.form.companyname'),
              label: t('creditcardstep.form.companyname'),
              error: errors.companyName
            }}
            onChange={handleChange}
            onBlur={handleBlur}
            size={16}
            autoFocus
            hasError={hasFieldError('companyName')}
          />

          {isEuropeanUnionCountry ? (
            <div className={styles['form__vat-fields']}>
              {country === 'PL' ? null : (
                <Row vertical='center' horizontal='center'>
                  <Paragraph size={16}>{country}</Paragraph>
                </Row>
              )}

              <div>
                <Input
                  id='vat'
                  value={vat}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  i18n={{
                    placeholder: t(getVATLabel(country)),
                    label: t(getVATLabel(country)),
                    error: errors.vat
                  }}
                  size={16}
                  hasError={hasFieldError('vat')}
                />

                <Tooltip
                  className={styles['form__vat-fields--tooltip']}
                  content={t(getVATTooltip(country))}
                >
                  <Icon icon='icon-info-circle' size={14} color='neutral-6' />
                </Tooltip>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {customerType === INDIVIDUAL ? (
        <Input
          id='nameAndSurname'
          value={nameAndSurname}
          i18n={{
            placeholder: t('creditcardstep.form.namesurname'),
            label: t('creditcardstep.form.namesurname'),
            error: errors.nameAndSurname
          }}
          onChange={handleChange}
          onBlur={handleBlur}
          size={16}
          autoFocus
          hasError={hasFieldError('nameAndSurname')}
        />
      ) : null}

      <Spacer space={40} />

      <Input
        id='address'
        value={address}
        i18n={{
          placeholder: t('creditcardstep.form.address'),
          label: t('creditcardstep.form.address'),
          error: errors.address
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        size={16}
        hasError={hasFieldError('address')}
      />

      <Spacer space={40} />

      <div className={styles['form__city-fields']}>
        {country === 'PL' ? (
          <MaskedInput
            id='postCode'
            value={postCode}
            mask={postCodeMask}
            i18n={{
              placeholder: t('creditcardstep.form.postcode.placeholder'),
              label: t('creditcardstep.form.postcode'),
              error: errors.postCode
            }}
            onChange={handleChange}
            onBlur={handleBlur}
            size={16}
            hasError={hasFieldError('postCode')}
          />
        ) : (
          <Input
            id='postCode'
            value={postCode}
            i18n={{
              placeholder: t('creditcardstep.form.postcode'),
              label: t('creditcardstep.form.postcode'),
              error: errors.postCode
            }}
            onChange={handleChange}
            onBlur={handleBlur}
            size={16}
            hasError={hasFieldError('postCode')}
          />
        )}

        <Input
          id='city'
          value={city}
          i18n={{
            placeholder: t('creditcardstep.form.city'),
            label: t('creditcardstep.form.city'),
            error: errors.city
          }}
          onChange={handleChange}
          onBlur={handleBlur}
          size={16}
          hasError={hasFieldError('city')}
        />
      </div>
    </Fragment>
  )
}

Form.displayName = 'CheckoutPaymentBillingForm'
